import { parseWeekSeries, getWeekDateRange } from './timeUtils';
import { getWeekNumber } from './helpers';

export const PLACEHOLDER_SUBJECT = '__PLACEHOLDER__';

export const DRAFT_STAT_PALETTE = [
    '#4F46E5',
    '#0891B2',
    '#059669',
    '#D97706',
    '#E11D48',
    '#7C3AED',
    '#0D9488',
    '#EA580C',
    '#2563EB',
    '#65A30D'
];

const OTHER_SLICE_COLOR = '#94A3B8';
const UNASSIGNED_COLOR = '#CBD5E1';

const isNonEmpty = (value) => typeof value === 'string' && value.trim() !== '';

const hashKey = (key) => {
    const text = String(key || '');
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
    }
    return hash;
};

export const colorForKey = (key, fallbackIndex = 0) => {
    if (!key) return DRAFT_STAT_PALETTE[fallbackIndex % DRAFT_STAT_PALETTE.length];
    return DRAFT_STAT_PALETTE[hashKey(key) % DRAFT_STAT_PALETTE.length];
};

const isValidHexColor = (value) => (
    typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim())
);

export const isPlaceholderEntry = (entry) => {
    const subject = entry?.subject || entry?.course || '';
    return String(subject).trim() === PLACEHOLDER_SUBJECT;
};

const occurrenceCount = (entry) => {
    const weeks = parseWeekSeries(entry?.weekSeries);
    return weeks.length > 0 ? weeks.length : 1;
};

const entryHours = (entry) => {
    const duration = Number(entry?.durationMinutes);
    if (!Number.isFinite(duration) || duration <= 0) return 0;
    return (duration / 60) * occurrenceCount(entry);
};

const teacherNamesForEntry = (entry) => {
    const fromArray = (entry?.teachersArray || [])
        .map((teacher) => teacher?.name)
        .filter(isNonEmpty);
    if (fromArray.length > 0) return fromArray;

    if (isNonEmpty(entry?.host)) return [entry.host.trim()];
    if (isNonEmpty(entry?.teacher)) return [entry.teacher.trim()];
    return [];
};

const groupLabelsForEntry = (entry) => {
    if (!Array.isArray(entry?.groups)) return [];
    return entry.groups.map((group) => {
        if (group == null) return '';
        if (typeof group === 'object') {
            return group.name || group.group_code || (group.id != null ? String(group.id) : '');
        }
        return String(group);
    }).filter(isNonEmpty);
};

const bumpMap = (map, key, label, hours, color) => {
    const existing = map.get(key);
    if (existing) {
        existing.count += 1;
        existing.hours += hours;
        return;
    }
    map.set(key, {
        key,
        label,
        count: 1,
        hours,
        color
    });
};

const mapToSortedSlices = (map) => (
    Array.from(map.values()).sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        if (b.hours !== a.hours) return b.hours - a.hours;
        return a.label.localeCompare(b.label);
    })
);

export const collapseSlices = (slices, metric = 'count', maxSlices = 6) => {
    const list = Array.isArray(slices) ? slices.filter((slice) => (slice?.[metric] || 0) > 0) : [];
    const sorted = [...list].sort((a, b) => {
        if ((b[metric] || 0) !== (a[metric] || 0)) return (b[metric] || 0) - (a[metric] || 0);
        return String(a.label).localeCompare(String(b.label));
    });

    if (sorted.length <= maxSlices) return sorted;

    const head = sorted.slice(0, maxSlices - 1);
    const tail = sorted.slice(maxSlices - 1);
    const other = {
        key: '__other__',
        label: 'Other',
        count: tail.reduce((sum, slice) => sum + (slice.count || 0), 0),
        hours: tail.reduce((sum, slice) => sum + (slice.hours || 0), 0),
        color: OTHER_SLICE_COLOR
    };

    if ((other[metric] || 0) <= 0) return head;
    return [...head, other];
};

export const formatHours = (hours) => {
    const value = Number(hours);
    if (!Number.isFinite(value) || value <= 0) return '0 h';
    const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
    return `${rounded} h`;
};

export const formatEntryCount = (count) => {
    const value = Number(count);
    if (!Number.isFinite(value) || value <= 0) return '0 entries';
    if (value === 1) return '1 entry';
    return `${value.toLocaleString()} entries`;
};

const readPeriodBound = (period, camelKey, snakeKey) => {
    const raw = period?.[camelKey] ?? period?.[snakeKey];
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

export const buildPeriodWeekAxis = (period) => {
    if (!period) return [];
    const startWeek = readPeriodBound(period, 'startWeek', 'start_week');
    const endWeek = readPeriodBound(period, 'endWeek', 'end_week');
    const startYear = readPeriodBound(period, 'startWeekYear', 'start_week_year')
        ?? readPeriodBound(period, 'year', 'year');
    if (startWeek == null || endWeek == null || startYear == null) return [];

    if (startWeek <= endWeek) {
        return Array.from({ length: endWeek - startWeek + 1 }, (_, index) => ({
            week: startWeek + index,
            year: startYear
        }));
    }

    return [
        ...Array.from({ length: 53 - startWeek + 1 }, (_, index) => ({
            week: startWeek + index,
            year: startYear
        })),
        ...Array.from({ length: endWeek }, (_, index) => ({
            week: index + 1,
            year: startYear + 1
        }))
    ];
};

const weekNumbersFromSeries = (weekSeries) => {
    if (!weekSeries || !String(weekSeries).trim()) return null;
    return new Set(parseWeekSeries(weekSeries));
};

const isCancelledForWeek = (entry, week, year) => {
    if (typeof entry?.getExceptionForWeek !== 'function') return false;
    const exception = entry.getExceptionForWeek(week, year);
    return Boolean(exception?.changes?.cancelled);
};

const sortWeekNumbersAcademic = (weeks) => {
    const unique = [...new Set(weeks)].filter((week) => Number.isFinite(week)).sort((a, b) => a - b);
    const hasHigh = unique.some((week) => week >= 40);
    const hasLow = unique.some((week) => week <= 15);
    if (hasHigh && hasLow) {
        return [...unique.filter((week) => week >= 40), ...unique.filter((week) => week < 40)];
    }
    return unique;
};

const inferWeekAxisFromEntries = (entries) => {
    const weekNumbers = [];
    entries.forEach((entry) => {
        const covered = weekNumbersFromSeries(entry?.weekSeries);
        if (!covered) return;
        covered.forEach((week) => weekNumbers.push(week));
    });
    return sortWeekNumbersAcademic(weekNumbers).map((week) => ({ week, year: null }));
};

const isoYearForDate = (date) => {
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    utc.setUTCDate(utc.getUTCDate() + 4 - (utc.getUTCDay() || 7));
    return utc.getUTCFullYear();
};

const currentIsoWeek = () => {
    const now = new Date();
    return { week: getWeekNumber(now), year: isoYearForDate(now) };
};

const decorateWeekCoord = (coord) => {
    const range = coord.year != null ? getWeekDateRange(coord.year, coord.week) : null;
    const monthLabel = range?.startDate
        ? range.startDate.toLocaleDateString('en-US', { month: 'short' })
        : '';
    const dateLabel = range?.startDate && range?.endDate
        ? `${range.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${range.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        : '';
    return { ...coord, monthLabel, dateLabel };
};

export const buildWeekCoverage = (entries = [], period = null) => {
    const realEntries = (Array.isArray(entries) ? entries : []).filter((entry) => !isPlaceholderEntry(entry));
    const axis = buildPeriodWeekAxis(period);
    const weeks = (axis.length > 0 ? axis : inferWeekAxisFromEntries(realEntries)).map(decorateWeekCoord);
    const today = currentIsoWeek();

    const coverage = weeks.map((coord) => {
        let count = 0;
        let hours = 0;

        realEntries.forEach((entry) => {
            const coveredWeeks = weekNumbersFromSeries(entry?.weekSeries);
            if (coveredWeeks && !coveredWeeks.has(coord.week)) return;
            if (isCancelledForWeek(entry, coord.week, coord.year)) return;

            count += 1;
            const duration = Number(entry?.durationMinutes);
            if (Number.isFinite(duration) && duration > 0) {
                hours += duration / 60;
            }
        });

        const isCurrent = coord.year != null
            ? coord.week === today.week && coord.year === today.year
            : coord.week === today.week;

        return {
            ...coord,
            count,
            hours,
            isCurrent
        };
    });

    const coveredCount = coverage.filter((week) => week.count > 0).length;
    return {
        weeks: coverage,
        totalWeeks: coverage.length,
        coveredCount,
        emptyCount: Math.max(0, coverage.length - coveredCount)
    };
};

export const buildDraftVersionStats = (entries = [], { period } = {}) => {
    const realEntries = (Array.isArray(entries) ? entries : []).filter((entry) => !isPlaceholderEntry(entry));
    const weekCoverage = buildWeekCoverage(realEntries, period);

    const subjectMap = new Map();
    const roomMap = new Map();
    const teacherMap = new Map();
    const groupSet = new Set();
    const weekSet = new Set();
    const dayCounts = new Map();
    let classCount = 0;
    let eventCount = 0;
    let withRoom = 0;
    let withTeacher = 0;
    let withGroups = 0;
    let withTime = 0;
    let totalHours = 0;

    realEntries.forEach((entry) => {
        const hours = entryHours(entry);
        totalHours += hours;

        const subjectLabel = isNonEmpty(entry.displayName)
            ? entry.displayName.trim()
            : (isNonEmpty(entry.subject) ? entry.subject.trim() : 'Untitled');
        const subjectKey = subjectLabel.toLowerCase();
        const subjectColor = isValidHexColor(entry?.subjectObj?.color)
            ? entry.subjectObj.color
            : colorForKey(subjectKey);
        bumpMap(subjectMap, subjectKey, subjectLabel, hours, subjectColor);

        const roomLabel = isNonEmpty(entry.room) ? entry.room.trim() : 'Unassigned';
        const roomKey = isNonEmpty(entry.room) ? entry.room.trim().toLowerCase() : '__unassigned__';
        bumpMap(roomMap, roomKey, roomLabel, hours, roomKey === '__unassigned__' ? UNASSIGNED_COLOR : colorForKey(roomKey));
        if (isNonEmpty(entry.room)) withRoom += 1;

        const teachers = teacherNamesForEntry(entry);
        if (teachers.length > 0) {
            withTeacher += 1;
            teachers.forEach((name) => {
                const key = name.trim().toLowerCase();
                bumpMap(teacherMap, key, name.trim(), hours, colorForKey(key));
            });
        } else {
            bumpMap(teacherMap, '__unassigned__', 'Unassigned', hours, UNASSIGNED_COLOR);
        }

        const groups = groupLabelsForEntry(entry);
        if (groups.length > 0) {
            withGroups += 1;
            groups.forEach((label) => groupSet.add(label));
        }

        if (isNonEmpty(entry.timeStart) && isNonEmpty(entry.timeEnd)) withTime += 1;

        const coveredWeeks = weekNumbersFromSeries(entry.weekSeries);
        if (coveredWeeks) {
            coveredWeeks.forEach((week) => weekSet.add(week));
        } else {
            weekCoverage.weeks.forEach((coord) => {
                if (!isCancelledForWeek(entry, coord.week, coord.year)) weekSet.add(coord.week);
            });
        }

        const dayKey = isNonEmpty(entry.day) ? entry.day.trim().toLowerCase() : 'unscheduled';
        const dayEntry = dayCounts.get(dayKey) || { day: dayKey, count: 0, hours: 0 };
        dayEntry.count += 1;
        dayEntry.hours += hours;
        dayCounts.set(dayKey, dayEntry);

        if (String(entry.type || '').toLowerCase() === 'event') eventCount += 1;
        else classCount += 1;
    });

    return {
        entryCount: realEntries.length,
        totalHours,
        uniqueSubjects: subjectMap.size,
        uniqueRooms: [...roomMap.keys()].filter((key) => key !== '__unassigned__').length,
        uniqueTeachers: [...teacherMap.keys()].filter((key) => key !== '__unassigned__').length,
        uniqueGroups: groupSet.size,
        uniqueWeeks: weekCoverage.coveredCount,
        weekNumbers: [...weekSet].sort((a, b) => a - b),
        weekCoverage,
        days: [...dayCounts.values()].sort((a, b) => b.count - a.count),
        types: { class: classCount, event: eventCount },
        completeness: {
            withRoom,
            withTeacher,
            withGroups,
            withTime
        },
        subjects: mapToSortedSlices(subjectMap),
        rooms: mapToSortedSlices(roomMap),
        teachers: mapToSortedSlices(teacherMap)
    };
};
